<template>
  <div class="fixed inset-0 z-50 overflow-y-auto">
    <!-- Backdrop -->
    <div 
      class="fixed inset-0 bg-black bg-opacity-50 transition-opacity"
      @click="$emit('close')"
    ></div>

    <!-- Modal -->
    <div class="flex min-h-full items-center justify-center p-4">
      <div class="relative bg-white rounded-lg shadow-xl max-w-md w-full p-6">
        <!-- Header -->
        <div class="flex items-center justify-between mb-6">
          <div>
            <h2 class="text-xl font-semibold text-gray-900">Create Account</h2>
            <p class="text-sm text-green-600 mt-1 flex items-center"><PhSparkle class="inline w-4 h-4 text-yellow-500 mr-1" weight="fill" /> Get 500 free Sparks on signup!</p>
          </div>
          <button
            @click="$emit('close')"
            class="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <PhX :size="24" />
          </button>
        </div>

        <!-- Signup Form -->
        <form @submit.prevent="handleSubmit" class="space-y-4">
          <!-- Error Message -->
          <div v-if="error" class="bg-red-50 border border-red-200 rounded-md p-4">
            <div class="flex">
              <PhWarning :size="20" class="text-red-400 flex-shrink-0" />
              <div class="ml-3">
                <p class="text-sm text-red-800">{{ error }}</p>
              </div>
            </div>
          </div>

          <!-- Success Message -->
          <div v-if="success" class="bg-green-50 border border-green-200 rounded-md p-4">
            <div class="flex">
              <PhCheckCircle :size="20" class="text-green-400 flex-shrink-0" />
              <div class="ml-3">
                <p class="text-sm text-green-800">{{ success }}</p>
              </div>
            </div>
          </div>

          <!-- Display Name -->
          <div>
            <label for="displayName" class="block text-sm font-medium text-gray-700 mb-1">
              Full Name
            </label>
            <input
              id="displayName"
              v-model="displayName"
              type="text"
              required
              class="input"
              placeholder="Enter your full name"
              :disabled="isLoading"
            />
          </div>

          <!-- Email -->
          <div>
            <label for="signupEmail" class="block text-sm font-medium text-gray-700 mb-1">
              Email address
            </label>
            <input
              id="signupEmail"
              v-model="email"
              type="email"
              required
              class="input"
              placeholder="Enter your email"
              :disabled="isLoading"
            />
          </div>

          <!-- Password -->
          <div>
            <label for="signupPassword" class="block text-sm font-medium text-gray-700 mb-1">
              Password
            </label>
            <input
              id="signupPassword"
              v-model="password"
              type="password"
              required
              minlength="6"
              class="input"
              placeholder="Choose a password (min 6 characters)"
              :disabled="isLoading"
            />
          </div>

          <!-- Confirm Password -->
          <div>
            <label for="confirmPassword" class="block text-sm font-medium text-gray-700 mb-1">
              Confirm Password
            </label>
            <input
              id="confirmPassword"
              v-model="confirmPassword"
              type="password"
              required
              class="input"
              placeholder="Confirm your password"
              :disabled="isLoading"
            />
          </div>

          <!-- Terms -->
          <div class="flex items-start">
            <input
              id="acceptTerms"
              v-model="acceptTerms"
              type="checkbox"
              required
              class="h-4 w-4 text-primary focus:ring-primary border-gray-300 rounded mt-1"
              :disabled="isLoading"
            />
            <label for="acceptTerms" class="ml-2 block text-sm text-gray-700">
              I agree to the 
              <a href="#" class="text-primary hover:text-primary/80">Terms of Service</a>
              and 
              <a href="#" class="text-primary hover:text-primary/80">Privacy Policy</a>
            </label>
          </div>

          <!-- Submit Button -->
          <button
            type="submit"
            :disabled="isLoading"
            class="w-full btn-primary flex items-center justify-center"
          >
            <div
              v-if="isLoading"
              class="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"
            ></div>
            {{ isLoading ? 'Creating account...' : 'Create account' }}
          </button>
        </form>

        <!-- Login Link -->
        <div class="mt-6 text-center">
          <p class="text-sm text-gray-600">
            Already have an account?
            <button
              @click="$emit('close')"
              class="text-primary hover:text-primary/80 font-medium"
            >
              Sign in
            </button>
          </p>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref } from 'vue'
import { PhX, PhWarning, PhCheckCircle, PhSparkle } from "@phosphor-icons/vue"
import { useAuthStore } from '../stores/auth'

const emit = defineEmits<{
  close: []
  success: []
}>()

const authStore = useAuthStore()

// Form state
const displayName = ref('')
const email = ref('')
const password = ref('')
const confirmPassword = ref('')
const acceptTerms = ref(false)
const isLoading = ref(false)
const error = ref('')
const success = ref('')

// Methods
async function handleSubmit() {
  error.value = ''
  success.value = ''

  // Validation
  if (!displayName.value || !email.value || !password.value || !confirmPassword.value) {
    error.value = 'Please fill in all fields'
    return
  }

  if (password.value !== confirmPassword.value) {
    error.value = 'Passwords do not match'
    return
  }

  if (password.value.length < 6) {
    error.value = 'Password must be at least 6 characters'
    return
  }

  if (!acceptTerms.value) {
    error.value = 'Please accept the terms and conditions'
    return
  }

  isLoading.value = true

  try {
    const result = await authStore.signup(email.value, password.value, displayName.value)
    
    if (result.success) {
      if (result.message) {
        success.value = result.message
        // Keep modal open to show success message
        setTimeout(() => {
          emit('success')
        }, 2000)
      } else {
        // Immediate login (no email confirmation)
        emit('success')
      }
    } else {
      error.value = result.error || 'Signup failed'
    }
  } catch (err) {
    error.value = 'An unexpected error occurred'
    console.error('Signup error:', err)
  } finally {
    isLoading.value = false
  }
}
</script>